import { useSyncExternalStore } from "react";

/**
 * Minimal hash router. The handoff explicitly forbids a routing dependency
 * without approval, so this is ~40 lines of native history + hash handling.
 * Routes are #/path?query. The auth flow deliberately keeps the magic token
 * in the hash fragment (not history), moved into memory on the confirm page.
 */

type Listener = () => void;

function readPath(): string {
  const hash = window.location.hash.replace(/^#/, "");
  const qIdx = hash.indexOf("?");
  return qIdx >= 0 ? hash.slice(0, qIdx) || "/" : hash || "/";
}

function readQueryString(): string {
  const hash = window.location.hash.replace(/^#/, "");
  const qIdx = hash.indexOf("?");
  return qIdx >= 0 ? hash.slice(qIdx + 1) : "";
}

// useSyncExternalStore compares snapshots by reference (Object.is). Building
// a new URLSearchParams each call would look like a change on every render,
// so cache the instance keyed by the raw query string.
let cachedQueryKey = "";
let cachedParams: URLSearchParams | null = null;

function readQueryStable(): URLSearchParams {
  const key = readQueryString();
  if (cachedParams === null || key !== cachedQueryKey) {
    cachedQueryKey = key;
    cachedParams = new URLSearchParams(key);
  }
  return cachedParams;
}

const listeners = new Set<Listener>();

function notify() {
  for (const l of listeners) l();
}

export function navigate(path: string, opts?: { replace?: boolean; query?: Record<string, string> }) {
  let target = `#${path.startsWith("/") ? path : `/${path}`}`;
  if (opts?.query) {
    const qs = new URLSearchParams(opts.query).toString();
    target += qs ? `?${qs}` : "";
  }
  if (opts?.replace) {
    window.location.replace(target);
  } else {
    window.location.hash = target;
  }
}

export function useRoute() {
  const path = useSyncExternalStore(
    (cb) => {
      const handler = () => {
        notify();
        cb();
      };
      window.addEventListener("hashchange", handler);
      listeners.add(handler);
      return () => {
        window.removeEventListener("hashchange", handler);
        listeners.delete(handler);
      };
    },
    readPath,
  );
  return path;
}

export function useRouteQuery(): URLSearchParams {
  return useSyncExternalStore(
    (cb) => {
      const handler = () => {
        notify();
        cb();
      };
      window.addEventListener("hashchange", handler);
      listeners.add(handler);
      return () => {
        window.removeEventListener("hashchange", handler);
        listeners.delete(handler);
      };
    },
    readQueryStable,
  );
}
